import { useEffect, useRef, useState, type CSSProperties } from "react";

// Drop-in replacement for @component/blob in the Trainer voice flow.
// Same props contract as the old WebGL Blob ({ getVolume, isActive, isSpeaking })
// so the existing audio reactivity keeps working untouched — the only change is
// the visual: a CSS conic-gradient "Siri"-style orb instead of the shader blob.
//
// Reactivity: `getVolume()` returns a smoothed 0..1 level (mic input while the
// user talks, TTS output while the AI talks). We poll it every frame and drive
// scale + glow + swirl speed off it, exactly the signal the old Blob consumed.
//
// Mode colors are preserved (so you can still tell who has the floor):
//   idle      → calm pale blue      (no active session)
//   listening → vivid blue / cyan   (user's turn)
//   speaking  → deep navy blue      (AI is talking)

interface Props {
  getVolume: () => number;
  isActive: boolean;
  isSpeaking: boolean;
  /**
   * Tailwind width/height classes for the orb box. Defaults to the ~14rem
   * setup-tile size; the live session passes a larger value.
   */
  sizeClassName?: string;
}

type OrbColors = { bg: string; c1: string; c2: string; c3: string };

// bg matches the dark tile the orb sits on (gray-900) so the gaps between the
// color lobes blend into the surface instead of showing as light halos.
const SURFACE_BG = "#111827";

const COLORS_IDLE: OrbColors = {
  bg: SURFACE_BG,
  c1: "oklch(70% 0.09 250)",
  c2: "oklch(82% 0.06 220)",
  c3: "oklch(60% 0.10 262)",
};

const COLORS_LISTENING: OrbColors = {
  bg: SURFACE_BG,
  c1: "oklch(58% 0.27 256)",
  c2: "oklch(86% 0.15 198)",
  c3: "oklch(48% 0.24 262)",
};

// AI-speaking: a deeper, more indigo blue — same family as listening but shifted
// cooler/richer and without the bright cyan highlight, so the turn-change is a
// gentle, natural shift rather than a drastic jump to dark navy.
const COLORS_SPEAKING: OrbColors = {
  bg: SURFACE_BG,
  c1: "oklch(46% 0.24 264)",
  c2: "oklch(60% 0.17 236)",
  c3: "oklch(38% 0.22 270)",
};

function ReactiveOrb({
  getVolume,
  isActive,
  isSpeaking,
  sizeClassName = "h-[min(14rem,55vw)] w-[min(14rem,55vw)]",
}: Props) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const orbRef = useRef<HTMLDivElement | null>(null);

  // Measured pixel size of the (responsive) box, fed into the Siri filter math.
  const [size, setSize] = useState(224);

  // Keep the latest getVolume readable inside the rAF loop without restarting
  // it — call sites hand us a fresh closure on every render.
  const getVolumeRef = useRef(getVolume);
  useEffect(() => {
    getVolumeRef.current = getVolume;
  }, [getVolume]);

  const colors = !isActive
    ? COLORS_IDLE
    : isSpeaking
      ? COLORS_SPEAKING
      : COLORS_LISTENING;

  // Track the rendered size so the blur/contrast/dot filters scale with the orb.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const s = Math.min(el.clientWidth, el.clientHeight);
      if (s > 0) setSize(s);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Single animation loop for the lifetime of the component.
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let t = 0; // organic "morph time", always advancing
    let level = 0;

    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;

      let target = 0;
      try {
        target = getVolumeRef.current();
      } catch {
        target = 0;
      }
      target = Math.min(1, Math.max(0, target));

      // Fast attack, slower release, so it tracks speech naturally.
      const rate = target > level ? dt * 20 : dt * 6;
      level += (target - level) * Math.min(1, rate);

      // Fluid, Siri-like motion: two lobe groups are steered by independent sums
      // of slow sine waves (not one monotonic angle), so the colors drift and
      // merge organically instead of spinning like a wheel. Audio nudges the
      // morph speed and amplitude — it never sets the orb rotating.
      t += dt * (0.22 + level * 0.4);
      const amp = 1 + level * 0.5;
      const a1 = amp * (60 * Math.sin(t * 0.7) + 34 * Math.sin(t * 0.31 + 1.7));
      const a2 = amp * (52 * Math.sin(t * 0.53 + 2.1) + 30 * Math.sin(t * 0.24 + 0.6));

      const el = orbRef.current;
      if (el) {
        el.style.setProperty("--a1", a1.toFixed(2) + "deg");
        el.style.setProperty("--a2", a2.toFixed(2) + "deg");
        el.style.transform = `scale(${(1 + level * 0.12).toFixed(4)})`;
        // Soft halo — kept subtle so it reads as a gentle bloom, not a hard
        // white ring. Low base opacity, and it only lifts a little with volume.
        const op = (0.04 + level * 0.16).toFixed(3);
        const bl = (22 + level * 26).toFixed(1);
        const sp = (0 + level * 2).toFixed(1);
        el.style.boxShadow = `0 0 ${bl}px ${sp}px rgba(255,255,255,${op})`;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Responsive filter values, mirroring the original SiriOrb math.
  const blur = Math.max(size * 0.015, 4);
  const contrast = Math.max(size * 0.008, 1.5);
  const dot = Math.max(size * 0.008, 0.1);
  const shadow = Math.max(size * 0.008, 2);
  const mask = size < 100 ? "15%" : "25%";

  return (
    <>
      <style>{`
        @property --a1 { syntax: "<angle>"; inherits: true; initial-value: 0deg; }
        @property --a2 { syntax: "<angle>"; inherits: true; initial-value: 0deg; }
        .reactive-orb { display: grid; grid-template-areas: "stack"; overflow: hidden; border-radius: 50%; position: relative; will-change: transform; }
        .reactive-orb::before, .reactive-orb::after { content: ""; display: block; grid-area: stack; width: 100%; height: 100%; border-radius: 50%; }
        .reactive-orb::before {
          background:
            conic-gradient(from calc(var(--a1) * 1) at 25% 70%, var(--c3), transparent 20% 80%, var(--c3)),
            conic-gradient(from calc(var(--a2) * 1) at 45% 75%, var(--c2), transparent 30% 60%, var(--c2)),
            conic-gradient(from calc(var(--a1) * -1) at 80% 20%, var(--c1), transparent 40% 60%, var(--c1)),
            conic-gradient(from calc(var(--a2) * 1) at 15% 5%, var(--c2), transparent 10% 90%, var(--c2)),
            conic-gradient(from calc(var(--a1) * 1) at 20% 80%, var(--c1), transparent 10% 90%, var(--c1)),
            conic-gradient(from calc(var(--a2) * -1) at 85% 10%, var(--c3), transparent 20% 80%, var(--c3));
          box-shadow: inset var(--bg) 0 0 var(--shadow-spread) calc(var(--shadow-spread) * 0.2);
          filter: blur(var(--blur-amount)) contrast(var(--contrast-amount));
        }
        .reactive-orb::after {
          background-image: radial-gradient(circle at center, var(--bg) var(--dot-size), transparent var(--dot-size));
          background-size: calc(var(--dot-size) * 2) calc(var(--dot-size) * 2);
          backdrop-filter: blur(calc(var(--blur-amount) * 2)) contrast(calc(var(--contrast-amount) * 2));
          mix-blend-mode: overlay;
          mask-image: radial-gradient(black var(--mask-radius), transparent 75%);
        }
      `}</style>
      <div
        ref={boxRef}
        className={`relative flex items-center justify-center ${sizeClassName}`}
      >
        <div
          ref={orbRef}
          aria-hidden="true"
          className="reactive-orb"
          style={
            {
              width: size,
              height: size,
              "--bg": colors.bg,
              "--c1": colors.c1,
              "--c2": colors.c2,
              "--c3": colors.c3,
              "--blur-amount": `${blur}px`,
              "--contrast-amount": contrast,
              "--dot-size": `${dot}px`,
              "--shadow-spread": `${shadow}px`,
              "--mask-radius": mask,
              boxShadow: "0 0 22px 0 rgba(255,255,255,0.04)",
            } as CSSProperties
          }
        />
      </div>
    </>
  );
}

export default ReactiveOrb;
